import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  DfIconArticle16,
  DfIconAssessment16,
  DfIconEpisode16,
  DfIconBook16,
  DfIconCourse16,
  DfIconEvent16,
  DfIconPlay16,
  DfIconRegistry,
  DfIconVideo16,
} from '@lib/fresco';
import { WindowLayoutService } from '@app/shared/services/window-layout/window-layout.service';
import { TranslateService } from '@ngx-translate/core';
import { QueueService } from '@app/shared/services/queue.service';
import { ContentCardService } from '@app/shared/components/content/content-card/content-card.service';
import { AuthService } from '@dg/shared-services';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { FlexibleDate } from '@app/shared/models/core-api.model';

export interface QueueItem {
  reference: {
    inputType?: string;
    inputId?: number;
    providerId?: string;
    providerName?: string;
    title?: string;
    externalId?: string;
  };
  dateDue: string;
  userQueueId: string;
  referenceType: string;
  referenceId: number;
  displayDate: string;
  dateCreated?: FlexibleDate;
}

@Component({
  selector: 'dgx-profile-saved',
  templateUrl: './profile-saved.component.html',
  styleUrls: ['./profile-saved.component.css'],
})
export class ProfileSavedComponent
  extends SubscriberBaseDirective
  implements OnInit, OnDestroy
{
  public readonly CLASS_NAME = 'ProfileAssignmentsComponent';

  @Input() public rowLength: number;

  // save is the variable the NgFor will use to store the list of saved items
  public saved = [];
  public hideExtension =
    this.authService?.authUser?.defaultOrgInfo?.settings?.restrictExtension;
  public pageLoading: boolean = true;
  public i18n = this.translateService.instant([
    'QueueCtrl_NoItemsSaved',
    'QueueCtrl_RemoveSavedItems',
    'QueueCtrl_SaveInstructionsFormat',
    'QueueCtrl_SaveUrlForLater',
    'QueueCtrl_EmptyStateTitle',
    'QueueCtrl_EmptyStateDescription',
    'QueueCtrl_SavedDescription',
  ]);

  public savedHeader = this.translateService.instant('Core_Saved');

  public settingsUrl = '/me/settings#';
  public noItemsPlaceholder = this.hideExtension
    ? ''
    : this.translateService.instant('QueueCtrl_SaveInstructionsFormat', {
        startBookmarklet: `<a href="${this.settingsUrl}/extensions"
          class="callout no-wrap"
          target="${this.windowLayoutService.isIframe ? '_blank' : '_self'}">`,
        startLearning: `<a href="/"
                        class="callout no-wrap"
                        target="${
                          this.windowLayoutService.isIframe ? '_blank' : '_self'
                        }">`,
        endTag: '</a>',
      });

  constructor(
    private windowLayoutService: WindowLayoutService,
    private translateService: TranslateService,
    private queueService: QueueService,
    private iconRegistry: DfIconRegistry,
    private cdr: ChangeDetectorRef,
    public contentCardService: ContentCardService,
    public authService: AuthService
  ) {
    super();
    this.iconRegistry.registerIcons([
      DfIconArticle16,
      DfIconAssessment16,
      DfIconEpisode16,
      DfIconBook16,
      DfIconEvent16,
      DfIconCourse16,
      DfIconPlay16,
      DfIconVideo16,
    ]);
  }

  public ngOnInit(): void {
    this.getSavedInputs();

    this.queueService.notificationCountModified$
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => this.getSavedInputs());
  }

  public ngOnDestroy(): void {
    this.contentCardService.setIndexesOfExpandedCards([]);
  }

  public trackSavedByID(index, queueItem: QueueItem): number {
    return queueItem.referenceId;
  }

  private getSavedInputs(): void {
    this.queueService.get().subscribe((result) => {
      this.saved = result.items;
      this.pageLoading = false;
      this.cdr.detectChanges();
    });
  }
}
